import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

/**
 * Centralized API client with authentication and error handling
 *
 * Features:
 * - Automatic JWT token injection
 * - 401 response handling (redirect to login)
 * - Consistent error transformation
 * - Token refresh support (when backend supports it)
 */

const API_URL = import.meta.env.VITE_API_URL || '';

// Create axios instance with default config
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token to all requests
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle common errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ message?: string; errors?: string[] }>) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized — attempt silent token refresh via httpOnly cookie
    if (error.response?.status === 401) {
      if (originalRequest && !originalRequest.headers?.['X-Retry']) {
        try {
          const response = await axios.post(
            `${API_URL}/api/auth/refresh`,
            {},
            { withCredentials: true }
          );

          const { accessToken } = response.data;
          localStorage.setItem('accessToken', accessToken);

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            originalRequest.headers['X-Retry'] = 'true';
          }
          return apiClient(originalRequest);
        } catch {
          clearAuthAndRedirect();
        }
      } else {
        clearAuthAndRedirect();
      }
    }

    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      console.error('Access forbidden:', error.response.data?.message);
      // Could dispatch an event or use a toast notification here
    }

    // Transform error for consistent handling
    const apiError = createApiError(error);
    return Promise.reject(apiError);
  }
);

/**
 * Clear auth state and redirect to login
 */
function clearAuthAndRedirect(): void {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('user');

  // Only redirect if not already on login/auth pages
  const currentPath = window.location.pathname;
  if (!currentPath.startsWith('/login') && !currentPath.startsWith('/auth')) {
    window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
  }
}

/**
 * API Error class for consistent error handling
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly errors?: string[]
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Create ApiError from AxiosError
 */
function createApiError(error: AxiosError<{ message?: string; errors?: string[] }>): ApiError {
  const status = error.response?.status || 500;
  const message =
    error.response?.data?.message || error.message || 'An unexpected error occurred';
  const errors = error.response?.data?.errors;

  return new ApiError(message, status, errors);
}

/**
 * Get auth headers for use with fetch API (legacy support)
 */
export function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Helper to unwrap API response data
 * Most backend responses follow: { success: boolean, data: T }
 */
export function unwrapResponse<T>(response: { data: { success: boolean; data: T } }): T {
  return response.data.data;
}

export default apiClient;
